import {
  changeErrMsg,
  changeLoading,
  changeTxn,
  changeTxnGA,
} from "@/app/appSlice"
import axios, { AxiosError } from "axios"
import { RootState } from "./store"
import { DateTime } from "luxon"
import md5 from "crypto-js/md5"
import { Lang } from "@/autoImport/i18n"

const $axios = axios.create()
export const useSetUpAxios = () => {
  const lang = useAppSelector((state: RootState) => state.app.lang)
  // const clientType = useAppSelector((state: RootState) => state.app.clientType)
  const token = useAppSelector((state: RootState) => state.app.token)

  $axios.interceptors.request.use(
    (config) => {
      config.baseURL = import.meta.env.VITE_APP_API_DOMAIN
      // Do something before request is sent
      const data = {
        appID: import.meta.env.VITE_APP_APPID,
        lang,
        ver: APP_VERSION,
        // clientType,
      }
      config.params = {
        ...data,
        ...config.params,
      }
      if (token) config.headers.Authorization = `Bearer ${token}`
      const timestampmils = DateTime.now().toMillis()
      config.headers["x-storellet-timestamp"] = timestampmils
      config.headers["x-storellet-checksum"] = md5(
        timestampmils.toString() + import.meta.env.VITE_APP_API_SECRET,
      )
        .toString()
        .toLowerCase()
      if (!config.headers["Content-Type"]) {
        config.headers["Content-Type"] = false
      }
      config.withCredentials = true
      return config
    },
    function (error) {
      // Do something with request error
      console.error(error)
      return Promise.reject(error)
    },
  )
}

interface ApiResponse<T> {
  result: Number
  data: T
}

export type txnResponse = {
  brandName: string
  brandId: number
  transactionAmount: number
  pointChange: number
  shopName: string
  transactionDate: number
  brandLogo: string
  pointEarnDate: number | undefined
  pointEarned: number | undefined
  customRedirectUrl: string | undefined
}

export type earnPointResponse = {
  points: number
  hasEmail: boolean
  merchantWebsiteUrl: string | null
  customImageUrl: string | null
  customImageRedirectUrl: string | null
  customMessage: string | null
  userId: number | null
}

export type userPointDetailResponse = {
  points: number
  hasEmail: boolean
  customRedirectUrl: string | null
}

export type tncResponse = {
  tnc: string
}

export type privatePolicyResponse = {
  privatePolicy: string
}

export type txnGA = Omit<
  txnResponse,
  "pointChange" | "shopName" | "transactionDate" | "brandLogo"
> & {}

export const txnResponseKeys = [
  "brandName",
  // "brandId",
  // "shopName",
  "transactionDate",
  "transactionAmount",
  "pointChange",
] as const

export const formatTxnResponse = (k: string, v: string | number) => {
  switch (k) {
    case "transactionAmount":
      return `$${v}`
    case "transactionDate":

    default:
      return v
  }
}

export const formatDateTime = (v: string | number) => {
  return DateTime.fromMillis(typeof v === "number" ? v : parseInt(v)).toFormat(
    "yyyy/LL/dd hh:mm a",
  )
}

export const useTxnEnquiry = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const invoiceQRCode = useAppSelector(
    (state: RootState) => state.app.invoiceQRCode,
  )

  const pos = useAppSelector((state: RootState) => state.app.pos)

  let _invoiceQRCode = `s=${invoiceQRCode}`
  if (!!pos) _invoiceQRCode = `${_invoiceQRCode}&pos=${pos}`

  const api = () =>
    new Promise<null | txnResponse>(async (resolve) => {
      dispatch(changeLoading(true))
      try {
        const res = await $axios.post<ApiResponse<txnResponse>>(
          "/invoiceEnquiry",
          {},
          {
            params: {
              invoiceQRCode: _invoiceQRCode,
              pos,
            },
          },
        )

        document.title = res.data.data.brandName + " - Powered by Storellet"
        dispatch(changeTxn(res.data.data))
        dispatch(
          changeTxnGA({
            ...res.data.data,
          }),
        )
        resolve(res.data.data)
      } catch (e: any) {
        if (e?.response?.data?.error?.message) {
          dispatch(changeErrMsg(e.response.data.error.message))
        }
        navigate("/error")
        resolve(null)
      } finally {
        dispatch(changeLoading(false))
      }
    })
  return api
  // useEffect(() => {
  //   ;(async () => {
  //     const res = await api()
  //     setResult(res)
  //   })()
  // }, [])
  // return result
}

export type checkMemberReq = {
  phone: string
  brandId: number
}

export type checkMemberRes = {
  success: boolean
}

export const useCheckMemberAndOtp = () => {
  const dispatch = useAppDispatch()

  const api = ({ phone, brandId }: checkMemberReq) =>
    new Promise<null | checkMemberRes | string>(async (resolve, reject) => {
      dispatch(changeLoading(true))
      try {
        const res = await $axios.post<ApiResponse<checkMemberRes>>(
          "/requestOTPForRegister",
          {},
          {
            params: {
              phone: phone,
              brandID: brandId,
            },
          },
        )

        if (res.data.data.success) {
          resolve("OTP")
        } else {
          resolve(res.data.data)
        }
      } catch (e: any) {
        if (
          e?.response?.status === 409 &&
          e.response.data.error.msgCode === 110
        ) {
          resolve("REGISTERED")
        } else {
          reject(e.response.data.error.message)
        }
      } finally {
        dispatch(changeLoading(false))
      }
    })

  return api
}

export type earnNRegisterReq = {
  phone: string
  otp?: string
}

export const useEarnNRegister = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const invoiceQRCode = useAppSelector(
    (state: RootState) => state.app.invoiceQRCode,
  )

  const pos = useAppSelector((state: RootState) => state.app.pos)

  const api = ({ phone, otp }: earnNRegisterReq) =>
    new Promise<earnPointResponse | null>(async (resolve, reject) => {
      dispatch(changeLoading(true))
      try {
        const res = await $axios.post<ApiResponse<earnPointResponse>>(
          "/earnAndRegister",
          {},
          {
            params: {
              phone,
              otpCode: otp,
              invoiceQRCode,
              pos,
            },
            withCredentials: true,
          },
        )

        resolve(res.data.data)
      } catch (e: any) {
        if (
          e?.response?.data?.error?.msgCode &&
          [128, 111, 112, 110, 138, 188, 117, 141].includes(
            e?.response?.data?.error?.msgCode,
          )
        ) {
          dispatch(changeErrMsg("ERR_NO_RECORD"))
          navigate("/error")
          reject(null)
          return
        }

        if (e?.response?.data?.error?.message) {
          dispatch(changeErrMsg(e.response.data.error.message))
          navigate("/error")
          reject(null)
          return
        }

        navigate("/error")
        resolve(null)
      } finally {
        dispatch(changeLoading(false))
      }
    })

  return api
}

export const useGetTnc = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const api = (lang: Lang) =>
    new Promise<null | tncResponse>(async (resolve, reject) => {
      dispatch(changeLoading(true))
      try {
        const res = await $axios.get<ApiResponse<tncResponse>>(
          "/fastOnboarding/tnc",
          {
            params: {
              lang,
            },
          },
        )

        resolve(res.data.data)
      } catch (e: any) {
        navigate("/error")
        reject(null)
      } finally {
        dispatch(changeLoading(false))
      }
    })

  return api
}

export const useGetPrivatePolicy = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const api = (lang: Lang) =>
    new Promise<null | privatePolicyResponse>(async (resolve, reject) => {
      dispatch(changeLoading(true))
      try {
        const res = await $axios.get<ApiResponse<privatePolicyResponse>>(
          "/fastOnboarding/privatePolicy",
          {
            params: {
              lang,
            },
          },
        )
        resolve(res.data.data)
      } catch (e: any) {
        navigate("/error")
        reject(null)
      } finally {
        dispatch(changeLoading(false))
      }
    })

  return api
}

export const useUpdateEmailSubscription = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const api = (phone: string, email: string) =>
    new Promise<null>(async (resolve, reject) => {
      dispatch(changeLoading(true))
      try {
        await $axios.put<ApiResponse<null>>(
          "/fastOnboarding/emailSubscription",
          {
            phone,
            email,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )
        resolve(null)
      } catch (e: any) {
        if (e?.response?.data?.error?.message) {
          reject(e.response.data.error.message)
          return
        }

        navigate("/error")
      } finally {
        dispatch(changeLoading(false))
      }
    })

  return api
}

export const useSendOtp = () => {
  const phone = useAppSelector((state: RootState) => state.app.phone)

  const api = () =>
    new Promise<null>(async (resolve, reject) => {
      try {
        await $axios.post<ApiResponse<null>>(
          "/fastOnboarding/sendOtp",
          {
            phone,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )
        resolve(null)
      } catch (e: any) {
        reject(e.response.data.error.message)
      }
    })

  return api
}

export const getDeeplinkToMerchantPage = (brandId: number) => {
  const deeplinkHost = import.meta.env.VITE_APP_NEXT_PUBLIC_DEEPLINK_URL || ""

  return deeplinkHost + "/merchant?mid=" + brandId
}
